import { db } from '../config/firebase';
import { LifeEvent, Location } from '../types';
import { LifeEventType } from '../types/person';
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  orderBy,
  Timestamp,
  getDoc
} from 'firebase/firestore';
import { ChangeTrackingService } from './changeTrackingService';

// Extended LifeEvent interface to include photo and relationship linking
export interface ExtendedLifeEvent extends Omit<LifeEvent, 'createdAt'> {
  updatedAt?: Date;
  photoIds?: string[];
  relationshipIds?: string[];
  createdAt: Date | Timestamp;
}

// Form data interface for creating/updating life events
export interface LifeEventFormData {
  personId: string;
  type: LifeEventType;
  date?: string;
  location?: Location;
  description: string;
  sources?: string[];
  photoIds?: string[];
  relationshipIds?: string[];
}

/**
 * TimelineService - Manages life events for persons, including CRUD operations
 * and linking events to photos or relationships
 */
class TimelineService {
  private static readonly LIFE_EVENTS_COLLECTION = 'lifeEvents';

  /**
   * Creates a new life event document in Firestore
   * @param eventData Form data for the new life event
   * @param userId ID of the user creating the event
   * @param userName Name of the user creating the event (optional, defaults to 'Unknown User')
   * @param isAdmin Whether the user is an admin (optional, defaults to false)
   * @returns Promise<ExtendedLifeEvent> The created life event
   */
  static async createEvent(
    eventData: LifeEventFormData,
    userId?: string,
    userName?: string,
    isAdmin?: boolean
  ): Promise<ExtendedLifeEvent> {
    if (!eventData.personId) {
      throw new Error('Person ID is required to create a life event');
    }

    if (!eventData.type) {
      throw new Error('Event type is required to create a life event');
    }

    if (!eventData.description?.trim()) {
      throw new Error('Event description is required');
    }

    try {
      // Prepare the document data
      const documentData = {
        personId: eventData.personId,
        type: eventData.type,
        date: eventData.date || null,
        location: eventData.location || null,
        description: eventData.description.trim(),
        sources: eventData.sources || [],
        photoIds: eventData.photoIds || [],
        relationshipIds: eventData.relationshipIds || [],
        createdBy: eventData.personId, // In a real app, this would be the current user ID
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Add document to Firestore
      const docRef = await addDoc(collection(db, this.LIFE_EVENTS_COLLECTION), documentData);

      // Return the created event with the generated ID
      const createdEvent: ExtendedLifeEvent = {
        ...documentData,
        id: docRef.id,
        date: documentData.date || undefined, // Convert null to undefined for type compatibility
        location: documentData.location || undefined, // Convert null to undefined for type compatibility
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Log change to ChangeTrackingService
      if (userId) {
        try {
          await ChangeTrackingService.logChange(
            {
              entityType: 'lifeEvent',
              entityId: createdEvent.id,
              changeType: 'create',
              beforeState: null,
              afterState: createdEvent,
              changedFields: Object.keys(documentData),
              reason: `Life event created: ${eventData.type}`,
            },
            userId,
            userName || 'Unknown User',
            isAdmin || false
          );
        } catch (changeError) {
          console.error('Error logging life event change:', changeError);
          // Don't fail the operation if change tracking fails
        }
      }

      return createdEvent;

    } catch (error) {
      console.error('Error creating life event:', error);
      throw error instanceof Error ? error : new Error('Failed to create life event');
    }
  }

  /**
   * Updates an existing life event document
   * @param eventId The ID of the event to update
   * @param updates Partial data to update
   * @param userId ID of the user updating the event (optional)
   * @param userName Name of the user updating the event (optional)
   * @param isAdmin Whether the user is an admin (optional, defaults to false)
   * @returns Promise<ExtendedLifeEvent> The updated life event
   */
  static async updateEvent(
    eventId: string,
    updates: Partial<LifeEventFormData>,
    userId?: string,
    userName?: string,
    isAdmin?: boolean
  ): Promise<ExtendedLifeEvent> {
    if (!eventId) {
      throw new Error('Event ID is required to update a life event');
    }

    try {
      const eventRef = doc(db, this.LIFE_EVENTS_COLLECTION, eventId);
      
      // Check if event exists
      const eventDoc = await getDoc(eventRef);
      if (!eventDoc.exists()) {
        throw new Error(`Life event with ID ${eventId} not found`);
      }

      // Prepare update data, filtering out undefined values
      const updateData: any = {
        updatedAt: serverTimestamp()
      };

      // Only include fields that are actually being updated
      if (updates.type !== undefined) updateData.type = updates.type;
      if (updates.date !== undefined) updateData.date = updates.date;
      if (updates.location !== undefined) updateData.location = updates.location;
      if (updates.description !== undefined) {
        if (!updates.description?.trim()) {
          throw new Error('Event description cannot be empty');
        }
        updateData.description = updates.description.trim();
      }
      if (updates.sources !== undefined) updateData.sources = updates.sources;
      if (updates.photoIds !== undefined) updateData.photoIds = updates.photoIds;
      if (updates.relationshipIds !== undefined) updateData.relationshipIds = updates.relationshipIds;

      // Get before state for change tracking (before the update)
      const beforeState = userId ? eventDoc.data() : null;

      // Update the document
      await updateDoc(eventRef, updateData);

      // Fetch and return the updated document
      const updatedDoc = await getDoc(eventRef);
      const updatedData = updatedDoc.data();

      const updatedEvent = {
        id: updatedDoc.id,
        ...updatedData,
        createdAt: updatedData?.createdAt?.toDate() || new Date(),
        updatedAt: new Date()
      } as ExtendedLifeEvent;

      // Log change to ChangeTrackingService
      if (userId && beforeState) {
        try {
          await ChangeTrackingService.logChange(
            {
              entityType: 'lifeEvent',
              entityId: eventId,
              changeType: 'update',
              beforeState,
              afterState: updatedEvent,
              changedFields: Object.keys(updates),
              reason: 'Life event updated',
            },
            userId,
            userName || 'Unknown User',
            isAdmin || false
          );
        } catch (changeError) {
          console.error('Error logging life event change:', changeError);
          // Don't fail the operation if change tracking fails
        }
      }

      return updatedEvent;

    } catch (error) {
      console.error('Error updating life event:', error);
      throw error instanceof Error ? error : new Error('Failed to update life event');
    }
  }

  /**
   * Deletes a life event document from Firestore
   * @param eventId The ID of the event to delete
   * @param userId ID of the user deleting the event (optional)
   * @param userName Name of the user deleting the event (optional)
   * @param isAdmin Whether the user is an admin (optional, defaults to false)
   * @returns Promise<void>
   */
  static async deleteEvent(
    eventId: string,
    userId?: string,
    userName?: string,
    isAdmin?: boolean
  ): Promise<void> {
    if (!eventId) {
      throw new Error('Event ID is required to delete a life event');
    }

    try {
      const eventRef = doc(db, this.LIFE_EVENTS_COLLECTION, eventId);
      
      // Check if event exists before deleting
      const eventDoc = await getDoc(eventRef);
      if (!eventDoc.exists()) {
        throw new Error(`Life event with ID ${eventId} not found`);
      }

      // Get before state for change tracking
      const beforeState = userId ? eventDoc.data() : null;

      await deleteDoc(eventRef);

      // Log change to ChangeTrackingService
      if (userId && beforeState) {
        try {
          await ChangeTrackingService.logChange(
            {
              entityType: 'lifeEvent',
              entityId: eventId,
              changeType: 'delete',
              beforeState,
              afterState: null,
              changedFields: ['isDeleted'],
              reason: 'Life event deleted',
            },
            userId,
            userName || 'Unknown User',
            isAdmin || false
          );
        } catch (changeError) {
          console.error('Error logging life event change:', changeError);
          // Don't fail the operation if change tracking fails
        }
      }

    } catch (error) {
      console.error('Error deleting life event:', error);
      throw error instanceof Error ? error : new Error('Failed to delete life event');
    }
  }

  /**
   * Retrieves all life events for a specific person, ordered by date
   * @param personId The ID of the person
   * @returns Promise<ExtendedLifeEvent[]> Array of life events
   */
  static async getPersonTimeline(personId: string): Promise<ExtendedLifeEvent[]> {
    if (!personId) {
      throw new Error('Person ID is required to retrieve timeline');
    }

    try {
      // Query events for the specified person
      // Note: Removed orderBy to avoid requiring Firestore composite index
      // Sorting will be done in memory after fetching
      const eventsQuery = query(
        collection(db, this.LIFE_EVENTS_COLLECTION),
        where('personId', '==', personId)
      );

      const querySnapshot = await getDocs(eventsQuery);

      const events: ExtendedLifeEvent[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        events.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        } as ExtendedLifeEvent);
      });

      // Sort in memory by date (most recent first)
      events.sort((a, b) => {
        if (!a.date && !b.date) return 0;
        if (!a.date) return 1;
        if (!b.date) return -1;
        return b.date.localeCompare(a.date);
      });

      return events;

    } catch (error) {
      console.error('Error retrieving person timeline:', error);
      throw error instanceof Error ? error : new Error('Failed to retrieve person timeline');
    }
  }

  /**
   * Links a photo to a life event
   * @param eventId The ID of the event
   * @param photoId The ID of the photo to link
   * @param userId ID of the user performing the action (optional)
   * @param userName Name of the user performing the action (optional)
   * @param isAdmin Whether the user is an admin (optional, defaults to false)
   * @param photoUrl Optional URL for quick display
   * @returns Promise<ExtendedLifeEvent> The updated life event
   */
  static async linkEventToPhoto(
    eventId: string,
    photoId: string,
    userId?: string,
    userName?: string,
    isAdmin?: boolean,
    photoUrl?: string
  ): Promise<ExtendedLifeEvent> {
    if (!eventId || !photoId) {
      throw new Error('Event ID and Photo ID are required to link photo to event');
    }

    try {
      const eventRef = doc(db, this.LIFE_EVENTS_COLLECTION, eventId);
      const eventDoc = await getDoc(eventRef);

      if (!eventDoc.exists()) {
        throw new Error(`Life event with ID ${eventId} not found`);
      }

      const eventData = eventDoc.data();
      const currentPhotoIds = eventData.photoIds || [];

      // Check if photo is already linked
      if (currentPhotoIds.includes(photoId)) {
        console.warn(`Photo ${photoId} is already linked to event ${eventId}`);
        return {
          id: eventDoc.id,
          ...eventData,
          createdAt: eventData.createdAt?.toDate() || new Date(),
          updatedAt: eventData.updatedAt?.toDate() || new Date()
        } as ExtendedLifeEvent;
      }

      // Get before state for change tracking
      const beforeState = userId ? {
        id: eventDoc.id,
        ...eventData,
        createdAt: eventData.createdAt?.toDate() || new Date(),
        updatedAt: eventData.updatedAt?.toDate() || new Date()
      } : null;

      // Add photo ID to the array
      const updatedPhotoIds = [...currentPhotoIds, photoId];

      // Update the document
      await updateDoc(eventRef, {
        photoIds: updatedPhotoIds,
        updatedAt: serverTimestamp()
      });

      // Fetch and return the updated document
      const updatedDoc = await getDoc(eventRef);
      const updatedData = updatedDoc.data();

      const afterState = {
        id: updatedDoc.id,
        ...updatedData,
        createdAt: updatedData?.createdAt?.toDate() || new Date(),
        updatedAt: new Date()
      } as ExtendedLifeEvent;

      // Log change to ChangeTrackingService
      if (userId && beforeState) {
        try {
          await ChangeTrackingService.logChange(
            {
              entityType: 'lifeEvent',
              entityId: eventId,
              changeType: 'update',
              beforeState,
              afterState,
              changedFields: ['photoIds'],
              reason: `Photo linked to life event: ${photoId}`,
            },
            userId,
            userName || 'Unknown User',
            isAdmin || false
          );
        } catch (changeError) {
          console.error('Error logging life event photo link change:', changeError);
          // Don't fail the operation if change tracking fails
        }
      }

      return afterState;

    } catch (error) {
      console.error('Error linking photo to event:', error);
      throw error instanceof Error ? error : new Error('Failed to link photo to event');
    }
  }

  /**
   * Links a relationship to a life event
   * @param eventId The ID of the event
   * @param relationshipId The ID of the relationship to link
   * @param userId ID of the user performing the action (optional)
   * @param userName Name of the user performing the action (optional)
   * @param isAdmin Whether the user is an admin (optional, defaults to false)
   * @returns Promise<ExtendedLifeEvent> The updated life event
   */
  static async linkEventToRelationship(
    eventId: string,
    relationshipId: string,
    userId?: string,
    userName?: string,
    isAdmin?: boolean
  ): Promise<ExtendedLifeEvent> {
    if (!eventId || !relationshipId) {
      throw new Error('Event ID and Relationship ID are required to link relationship to event');
    }

    try {
      const eventRef = doc(db, this.LIFE_EVENTS_COLLECTION, eventId);
      const eventDoc = await getDoc(eventRef);

      if (!eventDoc.exists()) {
        throw new Error(`Life event with ID ${eventId} not found`);
      }

      const eventData = eventDoc.data();
      const currentRelationshipIds = eventData.relationshipIds || [];

      // Check if relationship is already linked
      if (currentRelationshipIds.includes(relationshipId)) {
        console.warn(`Relationship ${relationshipId} is already linked to event ${eventId}`);
        return {
          id: eventDoc.id,
          ...eventData,
          createdAt: eventData.createdAt?.toDate() || new Date(),
          updatedAt: eventData.updatedAt?.toDate() || new Date()
        } as ExtendedLifeEvent;
      }

      // Get before state for change tracking
      const beforeState = userId ? {
        id: eventDoc.id,
        ...eventData,
        createdAt: eventData.createdAt?.toDate() || new Date(),
        updatedAt: eventData.updatedAt?.toDate() || new Date()
      } : null;

      // Add relationship ID to the array
      const updatedRelationshipIds = [...currentRelationshipIds, relationshipId];

      // Update the document
      await updateDoc(eventRef, {
        relationshipIds: updatedRelationshipIds,
        updatedAt: serverTimestamp()
      });

      // Fetch and return the updated document
      const updatedDoc = await getDoc(eventRef);
      const updatedData = updatedDoc.data();

      const afterState = {
        id: updatedDoc.id,
        ...updatedData,
        createdAt: updatedData?.createdAt?.toDate() || new Date(),
        updatedAt: new Date()
      } as ExtendedLifeEvent;

      // Log change to ChangeTrackingService
      if (userId && beforeState) {
        try {
          await ChangeTrackingService.logChange(
            {
              entityType: 'lifeEvent',
              entityId: eventId,
              changeType: 'update',
              beforeState,
              afterState,
              changedFields: ['relationshipIds'],
              reason: `Relationship linked to life event: ${relationshipId}`,
            },
            userId,
            userName || 'Unknown User',
            isAdmin || false
          );
        } catch (changeError) {
          console.error('Error logging life event relationship link change:', changeError);
          // Don't fail the operation if change tracking fails
        }
      }

      return afterState;

    } catch (error) {
      console.error('Error linking relationship to event:', error);
      throw error instanceof Error ? error : new Error('Failed to link relationship to event');
    }
  }

  /**
   * Retrieves life events by type for a specific person
   * @param personId The ID of the person
   * @param eventType The type of events to retrieve
   * @returns Promise<ExtendedLifeEvent[]> Array of filtered life events
   */
  static async getEventsByType(personId: string, eventType: LifeEventType): Promise<ExtendedLifeEvent[]> {
    if (!personId || !eventType) {
      throw new Error('Person ID and event type are required');
    }

    try {
      const eventsQuery = query(
        collection(db, this.LIFE_EVENTS_COLLECTION),
        where('personId', '==', personId),
        where('type', '==', eventType),
        orderBy('date', 'desc')
      );

      const querySnapshot = await getDocs(eventsQuery);
      
      const events: ExtendedLifeEvent[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        events.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        } as ExtendedLifeEvent);
      });

      return events;

    } catch (error) {
      console.error('Error retrieving events by type:', error);
      throw error instanceof Error ? error : new Error('Failed to retrieve events by type');
    }
  }

  /**
   * Retrieves life events within a date range for a specific person
   * @param personId The ID of the person
   * @param startDate Start date (ISO string)
   * @param endDate End date (ISO string)
   * @returns Promise<ExtendedLifeEvent[]> Array of events within the date range
   */
  static async getEventsInDateRange(
    personId: string, 
    startDate: string, 
    endDate: string
  ): Promise<ExtendedLifeEvent[]> {
    if (!personId || !startDate || !endDate) {
      throw new Error('Person ID, start date, and end date are required');
    }

    try {
      const eventsQuery = query(
        collection(db, this.LIFE_EVENTS_COLLECTION),
        where('personId', '==', personId),
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date', 'asc')
      );

      const querySnapshot = await getDocs(eventsQuery);
      
      const events: ExtendedLifeEvent[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        events.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        } as ExtendedLifeEvent);
      });

      return events;

    } catch (error) {
      console.error('Error retrieving events in date range:', error);
      throw error instanceof Error ? error : new Error('Failed to retrieve events in date range');
    }
  }
}

export default TimelineService;